import {
  UNSAVED_CHANGES_SCOPE_PRIORITY,
  type UnsavedChangesScopeEnum,
} from '../../enums/UnsavedChangesScopeEnum';

export interface UnsavedChangesRegistration {
  id: symbol;
  scope: UnsavedChangesScopeEnum;
  isDirty: () => boolean;
  onDiscard?: () => void;
  registeredAt: number;
}

const registrations = new Map<symbol, UnsavedChangesRegistration>();

export function registerUnsavedChangesEntry(
  entry: Omit<UnsavedChangesRegistration, 'registeredAt'>,
): () => void {
  registrations.set(entry.id, { ...entry, registeredAt: Date.now() });

  return () => {
    registrations.delete(entry.id);
  };
}

export function getActiveUnsavedChangesEntry(): UnsavedChangesRegistration | null {
  const dirtyEntries = Array.from(registrations.values()).filter((entry) => entry.isDirty());

  if (dirtyEntries.length === 0) {
    return null;
  }

  return dirtyEntries.reduce((active, candidate) => {
    const activePriority = UNSAVED_CHANGES_SCOPE_PRIORITY[active.scope];
    const candidatePriority = UNSAVED_CHANGES_SCOPE_PRIORITY[candidate.scope];

    if (candidatePriority < activePriority) {
      return candidate;
    }

    if (candidatePriority > activePriority) {
      return active;
    }

    return candidate.registeredAt >= active.registeredAt ? candidate : active;
  });
}

export function hasUnsavedChangesInRegistry(): boolean {
  return getActiveUnsavedChangesEntry() !== null;
}

/** @internal Test helper */
export function clearUnsavedChangesRegistryForTests(): void {
  registrations.clear();
}

/** @internal Test helper */
export function getUnsavedChangesRegistrySizeForTests(): number {
  return registrations.size;
}
